import React, { useRef, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { 
  FileText, 
  Download, 
  Printer,
  Building2,
  Mail,
  Phone,
  Globe,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

// Sample order data for preview
const sampleOrder = {
  order_number: 'GRC-WEB-2016021255',
  legacy_order_number: 'OC-2016021255',
  created_at: new Date('2026-03-04T15:51:15Z'),
  status: 'completed',
  payment_status: 'paid',
  payment_method: 'Credit Card (Stripe)',
  customer_name: 'Pam Spencer',
  billing_email: 'pam.spencer@melham.org',
  billing_phone: '(308) 872-4100',
  billing_company: 'Melham Medical Center',
  billing_address: '145 Memorial Drive',
  billing_city: 'Broken Bow',
  billing_state: 'NE',
  billing_zip: '68822',
  billing_country: 'United States',
  items: [
    {
      webinar_code: 'DIXO-0015',
      webinar_title: 'The CMS Hospital Infection Prevention and Control and Antibiotic Stewardship Program: CMS Compliance Requirements 2026',
      variant: 'Live Webinar',
      quantity: 1,
      unit_price: 200.00,
      total_price: 200.00,
      scheduled_date: '2026-03-15',
      speaker: 'Dr. Sarah Mitchell'
    }
  ],
  subtotal: 200.00,
  discount_amount: 0,
  tax_amount: 0,
  total_amount: 200.00
};

// Default company info
const defaultCompanyInfo = {
  company_name: 'GRC EDUCATORS INC',
  address: '1312 17th Street, Unit #2229',
  city: 'Denver',
  state: 'Colorado',
  zip_code: '80202',
  country: 'USA',
  phone: '713-401-9995',
  email: 'support@grceducators.com',
  billing_email: 'events@grceducators.us',
  website: 'www.grceducators.com'
};

export default function InvoicePreview({ order = sampleOrder, onClose, hideActions = false }) {
  const invoiceRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(defaultCompanyInfo);
  
  const invoiceNumber = `INV-${order.order_number?.replace('GRC-WEB-', '') || '2016021255'}`;
  const invoiceDate = order.created_at ? format(new Date(order.created_at), 'MMMM dd, yyyy') : 'March 04, 2026';

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/public/company-info`);
      if (response.ok) {
        const data = await response.json();
        setCompanyInfo(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Failed to fetch company info:', error);
    }
  };

  // Build full address for display
  const fullAddressLine1 = companyInfo.address;
  const fullAddressLine2 = [
    companyInfo.city,
    companyInfo.state,
    companyInfo.zip_code,
    companyInfo.country
  ].filter(Boolean).join(', ');

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      // Dynamically import html2pdf to avoid SSR issues
      const html2pdf = (await import('html2pdf.js')).default;
      
      const element = invoiceRef.current;
      if (!element) {
        toast.error('Invoice content not found');
        return;
      }

      const opt = {
        margin: 0,
        filename: `${invoiceNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(element).save();
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to download invoice');
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    setPrinting(true);
    try {
      // Create a new window with just the invoice content
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow popups to print');
        setPrinting(false);
        return;
      }

      const invoiceContent = invoiceRef.current;
      if (!invoiceContent) {
        toast.error('Invoice content not found');
        setPrinting(false);
        return;
      }

      // Get all stylesheets
      const styles = Array.from(document.styleSheets)
        .map(styleSheet => {
          try {
            return Array.from(styleSheet.cssRules)
              .map(rule => rule.cssText)
              .join('\n');
          } catch (e) {
            return '';
          }
        })
        .join('\n');

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoice ${invoiceNumber}</title>
          <style>
            ${styles}
            @media print {
              body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
            body { margin: 0; padding: 0; background: white; }
          </style>
        </head>
        <body>
          ${invoiceContent.outerHTML}
        </body>
        </html>
      `);
      
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
        setPrinting(false);
      };
      
      // Fallback if onload doesn't fire
      setTimeout(() => {
        if (printWindow && !printWindow.closed) {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        }
        setPrinting(false);
      }, 1000);
      
    } catch (error) {
      console.error('Error printing:', error);
      toast.error('Failed to print invoice');
      setPrinting(false);
    }
  };
  
  return (
    <div className="bg-slate-100 py-8 px-4">
      {/* Action Bar - only show if not hidden */}
      {!hideActions && (
        <div className="max-w-4xl mx-auto mb-6 flex justify-between items-center print:hidden">
          <h1 className="text-2xl font-bold text-slate-800">Invoice Preview</h1>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="gap-2"
              onClick={handlePrint}
              disabled={printing}
              data-testid="print-invoice-btn"
            >
              {printing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Printer className="w-4 h-4" />
              )}
              Print
            </Button>
            <Button 
              className="gap-2 bg-slate-800 hover:bg-slate-900"
              onClick={handleDownloadPDF}
              disabled={downloading}
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download PDF
            </Button>
          </div>
        </div>
      )}

      {/* Invoice Document */}
      <div 
        ref={invoiceRef} 
        className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden"
        id="invoice-content"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-8" style={{background: 'linear-gradient(to right, #0f172a, #1e293b)'}}>
          <div className="flex justify-between items-start">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-4 mb-4">
                {/* Logo matching frontend */}
                <svg className="h-14" viewBox="0 0 280 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Arrow/Path Symbol */}
                  <g>
                    <path
                      d="M10 27 L28 8 L46 27 L28 19 L10 27Z"
                      fill="#D4A03A"
                    />
                    <path
                      d="M28 19 L46 27 L28 46 L28 36 L16 27 L28 19Z"
                      fill="#FFFFFF"
                      fillOpacity="0.9"
                    />
                  </g>
                  {/* GRC Text */}
                  <text
                    x="56"
                    y="35"
                    fontFamily="Arial, sans-serif"
                    fontSize="26"
                    fontWeight="700"
                    fill="#FFFFFF"
                  >
                    GRC
                  </text>
                  <text
                    x="110"
                    y="35"
                    fontFamily="Arial, sans-serif"
                    fontSize="26"
                    fontWeight="700"
                    fill="#D4A03A"
                  >
                    EDUCATORS
                  </text>
                  {/* Tagline */}
                  <text
                    x="56"
                    y="50"
                    fontFamily="Arial, sans-serif"
                    fontSize="9"
                    fill="#94A3B8"
                    letterSpacing="0.15em"
                  >
                    YOUR PATH TO EXCELLENCE
                  </text>
                </svg>
              </div>
              <div className="text-slate-300 text-sm space-y-1 ml-1">
                <p className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {fullAddressLine1}
                </p>
                <p className="ml-6">{fullAddressLine2}</p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {companyInfo.phone}
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {companyInfo.billing_email || companyInfo.email}
                </p>
                <p className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {companyInfo.website}
                </p>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="text-right">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-4" style={{backgroundColor: 'rgba(255,255,255,0.1)'}}>
                <FileText className="w-5 h-5" />
                <span className="text-xl font-bold">INVOICE</span>
              </div>
              <div className="space-y-2">
                <div>
                  <p className="text-slate-400 text-sm">Invoice Number</p>
                  <p className="font-semibold text-lg">{invoiceNumber}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Invoice Date</p>
                  <p className="font-semibold">{invoiceDate}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Order Reference</p>
                  <p className="font-mono text-sm">{order.order_number}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bill To & Payment Status */}
        <div className="p-8 border-b" style={{backgroundColor: '#f8fafc'}}>
          <div className="flex justify-between">
            {/* Bill To */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Bill To</h3>
              <div className="text-slate-800">
                <p className="font-semibold text-lg">{order.customer_name || order.billing_first_name + ' ' + order.billing_last_name}</p>
                {order.billing_company && (
                  <p className="text-slate-600">{order.billing_company}</p>
                )}
                <p className="text-slate-600">{order.billing_address}</p>
                <p className="text-slate-600">
                  {order.billing_city}, {order.billing_state} {order.billing_zip}
                </p>
                <p className="text-slate-600">{order.billing_country}</p>
                <p className="text-slate-600 mt-2">{order.billing_email}</p>
                {order.billing_phone && (
                  <p className="text-slate-600">{order.billing_phone}</p>
                )}
              </div>
            </div>

            {/* Payment Status */}
            <div className="text-right">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Payment Status</h3>
              <Badge className="text-sm px-4 py-2" style={{backgroundColor: '#d1fae5', color: '#047857'}}>
                <CheckCircle className="w-4 h-4 mr-2" />
                PAID
              </Badge>
              <p className="text-slate-500 text-sm mt-2">via {order.payment_method}</p>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="p-8">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Order Items</h3>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead style={{backgroundColor: '#f1f5f9'}}>
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Description</th>
                  <th className="text-center px-4 py-3 text-sm font-semibold text-slate-600 w-20">Qty</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600 w-28">Unit Price</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600 w-28">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {order.items.map((item, index) => (
                  <tr key={index} className="hover:bg-slate-50">
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium text-slate-800">{item.webinar_title}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs font-mono px-2 py-0.5 rounded" style={{backgroundColor: '#e2e8f0', color: '#475569'}}>
                            {item.webinar_code}
                          </span>
                          <span className="text-sm text-slate-500">{item.variant}</span>
                        </div>
                        {item.speaker && (
                          <p className="text-sm text-slate-500">
                            Speaker: {item.speaker}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center text-slate-600">{item.quantity}</td>
                    <td className="px-4 py-4 text-right text-slate-600">
                      ${(item.unit_price || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-4 text-right font-medium text-slate-800">
                      ${(item.total_price || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-6 flex justify-end">
            <div className="w-72">
              <div className="space-y-2">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span>
                  <span>${(order.subtotal || 0).toFixed(2)}</span>
                </div>
                {order.discount_amount > 0 && (
                  <div className="flex justify-between" style={{color: '#047857'}}>
                    <span>Discount</span>
                    <span>-${(order.discount_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                {order.tax_amount > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span>Tax</span>
                    <span>${(order.tax_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-lg font-bold text-slate-800">
                  <span>Total</span>
                  <span>${(order.total_amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-medium" style={{color: '#047857'}}>
                  <span>Amount Paid</span>
                  <span>${(order.total_amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-slate-800 px-3 py-2 rounded" style={{backgroundColor: '#f1f5f9'}}>
                  <span>Balance Due</span>
                  <span>$0.00</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-8" style={{backgroundColor: '#f8fafc'}}>
          <div className="grid grid-cols-2 gap-8">
            {/* Terms */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-2">Terms & Conditions</h4>
              <ul className="text-sm text-slate-500 space-y-1">
                <li>• Access to webinar content is granted upon payment confirmation.</li>
                <li>• Recordings are available for 12 months from purchase date.</li>
                <li>• Refunds are available within 7 days if webinar hasn't been accessed.</li>
              </ul>
            </div>
            
            {/* Support */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-2">Need Help?</h4>
              <p className="text-sm text-slate-500 mb-2">
                For billing inquiries or support, please contact us:
              </p>
              <p className="text-sm text-slate-600">
                <strong>Email:</strong> {companyInfo.email}
              </p>
              <p className="text-sm text-slate-600">
                <strong>Phone:</strong> {companyInfo.phone}
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Business Hours: Mon-Fri, 9 AM - 6 PM EST
              </p>
            </div>
          </div>

          <Separator className="my-6" />

          {/* Company Footer */}
          <div className="text-center text-sm text-slate-500">
            <p className="font-medium text-slate-600">Thank you for choosing {companyInfo.company_name}!</p>
            <p className="mt-1">
              {companyInfo.company_name} | {fullAddressLine1}, {fullAddressLine2}
            </p>
            <p className="mt-1 text-xs">
              This invoice was generated electronically and is valid without signature.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
